<script lang="ts" setup>
import { useI18n } from '#i18n'
import { home } from '~/queries/home'
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Navigation } from 'vue3-carousel'

const { locale } = useI18n()
const { t } = useI18n()

const { data, pending, error } = await useAsyncData('homepage', () =>
  execQuery(home)
)
const showPopup = ref(false)
const isFrench = ref(true)

const frTitle = "Bienvenue sur le nouveau site des ressources de l'Ircam !"
const enTitle = 'Welcome to the new Ircam resources website!'

const closePopup = () => {
  showPopup.value = false
  localStorage.setItem('visited', 'true')
}

const toggleLanguage = () => {
  isFrench.value = !isFrench.value
}

onBeforeMount(() => {
  if (!localStorage.getItem('visited')) {
    showPopup.value = true
  }
})

let searchState = useState('searchState')
searchState.value = ''

// Focus
const focusText = computed<string>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.focusText.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})
const focusTextEn = computed<string>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.focusTextEn.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})

function formatCard(item) {
  return {
    title: item.cardTitle,
    titleEn: item.cardTitleEn,
    subtitle: item.cardSubTitle,
    excerpt: item.cardDescription,
    excerptEn: item.cardDescriptionEn,
    image: item.cardImage,
    ...item,
  }
}

const focuses = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.focus
    .filter((focus) => focus)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

// Exhibition
const exhibitionText = computed<string>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.exhibitionText.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})
const exhibitionTextEn = computed<string>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.exhibitionTextEn.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})
const exhibition = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.exhibition
    .filter((item) => item)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

// Latest publications
const latestPublications = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.actualities
    .filter((actuality) => actuality)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})
//Floras
const floraText = computed<String>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.floraText.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})
const floraTextEn = computed<String>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.floraTextEn.replaceAll(
    '<a ',
    '<a target="_blank"'
  )
})
const floras = computed<ResourceCards>(() => {
  if (pending.value || error.value) return []
  return data.value.data.ressourceHome?.floras
    .filter((flora) => flora)
    .map(formatCard)
    .sort((a, b) => a.order - b.order)
})

const breakpoints = ref({
  700: {
    itemsToShow: 3,
    itemsToScroll: 2,
    snapAlign: 'center',
  },
  1024: {
    itemsToShow: 4,
    itemsToScroll: 2,
    snapAlign: 'start',
  },
})

const title = 'Ressources'
const description =
  'Le pôle documentaire regroupe le catalogue en ligne de la médiathèque, un accès aux bases documentaires et aux archives audiovisuelles de l’Ircam.'
useHead({
  title,
  meta: [
    {
      name: 'description',
      content: description,
    },
  ],
})

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
})

const library: Array<PersonBase> = [
  {
    title: t('index.collections.subtitle_6'),
    groups: [
      {
        title: t('index.collections.subsubtitle_1'),
        people: [
          {
            firstName: 'Aurore',
            lastName: 'Baudin',
            email: 'info-mediatheque@ircam.fr',
          },
          {
            firstName: 'Jérôme',
            lastName: 'Boutinot',
            email: 'info-mediatheque@ircam.fr',
          },
        ],
      },
      {
        title: t('index.collections.subsubtitle_2'),
        people: [
          {
            firstName: 'Jean-Paul',
            lastName: 'Rodrigues',
          },
        ],
      },
    ],
  },
]

const credits: Array<PersonBase> = [
  {
    title: t('index.credits.brahms_title'),
    subtext: t('index.credits.description_2'),
    groups: [
      {
        title: t('index.credits.editorial_manager'),
        people: [
          {
            firstName: 'Philippe',
            lastName: 'Langlois',
            email: 'info-mediatheque@ircam.fr',
          },
        ],
      },
      {
        title: t('index.credits.documentation_editorial_followup_relations'),
        people: [
          {
            firstName: 'Aurore',
            lastName: 'Baudin',
            email: 'info-mediatheque@ircam.fr',
          },
          {
            firstName: 'Jérôme',
            lastName: 'Boutinot',
            email: 'info-mediatheque@ircam.fr',
          },
        ],
      },
      {
        title: t('index.credits.reading_committee'),
        people: [
          { firstName: 'Laurent', lastName: 'Feneyrou' },
          { firstName: 'Fanny', lastName: 'Gribenski' },
          { firstName: 'Philippe', lastName: 'Langlois' },
          { firstName: 'Grégoire', lastName: 'Lorieux' },
          { firstName: 'Michèle', lastName: 'Tosi' },
          { firstName: 'Jonathan', lastName: 'Cross' },
        ],
      },
      {
        title: t('index.credits.it_design_development'),
        people: [
          {
            firstName: 'Laura',
            lastName: 'Fremy',
            email: 'laura.fremy@ircam.fr',
          },
          {
            firstName: 'Salah eddine',
            lastName: 'Chaouch',
            email: 'salaheddine.chaouch@ircam.fr',
          },
          {
            firstName: 'Jérémy',
            lastName: 'Paul',
            informations: 'Web Design',
          },
        ],
      },
      {
        title: t('index.credits.translators'),
        people: [
          {
            firstName: 'Annelies',
            lastName: 'Fryberger',
            informations: 'Supervision',
          },
          {
            firstName: 'Meagan',
            lastName: 'Mason',
            informations: 'Supervision',
          },
          { firstName: 'Peter', lastName: 'Asimov' },
          { firstName: 'Melvin', lastName: 'Backstrom' },
          { firstName: 'Paul', lastName: 'Clift' },
          { firstName: 'Regan', lastName: 'Kramer' },
          { firstName: 'Emmanuelle', lastName: 'Majeau-Bettez' },
          { firstName: 'Theo', lastName: 'Radford' },
          { firstName: 'Miranda', lastName: 'Richmond-Mouillot' },
          { firstName: 'Tadhg', lastName: 'Sauvey' },
        ],
      },
    ],
  },
  {
    title: t('index.credits.analysis_title'),
    groups: [
      {
        title: t('index.credits.editorial_management'),
        people: [
          {
            firstName: 'Alain',
            lastName: 'Bonardi',
            informations: 'Université Paris VIII, STMS-Ircam',
          },
          {
            firstName: 'François-Xavier',
            lastName: 'Féron',
            email: 'info-mediatheque@ircam.fr',
          },
        ],
      },
      {
        title: t('index.credits.scientific_committee'),
        people: [
          {
            firstName: 'Bruno',
            lastName: 'Bossis',
            informations: 'Université Rennes II',
          },
          {
            firstName: 'Guillaume',
            lastName: 'Boutard',
            informations: 'Université de Montréal',
          },
          {
            firstName: 'Frédéric',
            lastName: 'Dufeu',
            informations: 'University of Huddersfield',
          },
          {
            firstName: 'Vincent',
            lastName: 'Tiffon',
            informations: 'Aix-Marseille Université',
          },
          {
            firstName: 'Laurent',
            lastName: 'Pottier',
            informations: 'Université Jean-Monnet de Saint-Etienne',
          },
          {
            firstName: 'Bruno',
            lastName: 'Bossis',
            informations: 'Université Rennes II',
          },
          {
            firstName: 'Serge',
            lastName: 'Lemouton',
            informations: 'Réalisateur en informatique musicale',
          },
          { firstName: 'Julie', lastName: 'Delisle' },
          { firstName: 'Eric', lastName: 'Maestri' },
          { firstName: 'João', lastName: 'Svidzinski' },
          { firstName: 'Laura', lastName: 'Zattra' },
          { firstName: 'Miriam', lastName: 'Akkermann' },
        ],
      },
    ],
  },
  {
    title: t('index.credits.editorial_technical_team'),
    groups: [
      {
        title: null,
        people: [
          {
            firstName: 'Aurore',
            lastName: 'Baudin',
            informations: 'Ircam – Pôle documentaire',
          },
          {
            firstName: 'Jérôme',
            lastName: 'Boutinot',
            informations: 'Ircam – Pôle documentaire',
          },
          {
            firstName: 'Laura',
            lastName: 'Frémy',
            informations: 'Ircam – Développement web',
          },
          {
            firstName: 'Salah eddine',
            lastName: 'Chaouch',
            informations: 'Ircam – Développement web',
          },
        ],
      },
    ],
  },
  {
    title: t('index.credits.medias_title'),
    groups: [
      {
        title: t('index.credits.editorial_manager'),
        people: [
          {
            firstName: 'Eric',
            lastName: 'de Gélis',
          },
        ],
      },
      {
        title: t('index.credits.production_title'),
        people: [
          {
            firstName: 'Eric',
            lastName: 'de Gélis',
          },
          {
            firstName: 'Bastien',
            lastName: 'Sabarros',
          },
        ],
      },
      {
        title: t('index.credits.editorial_technical_team'),
        people: [
          {
            firstName: 'Aurore',
            lastName: 'Baudin',
          },
          {
            firstName: 'Jérôme',
            lastName: 'Boutinot',
          },
        ],
      },
    ],
  },
  {
    title: t('index.credits.sidney_title'),
    groups: [
      {
        title: t('index.credits.editorial_management'),
        people: [
          {
            firstName: 'Cyril',
            lastName: 'Beros',
          },
          {
            firstName: 'Serge',
            lastName: 'Lemouton',
          },
        ],
      },
      {
        title: t('index.credits.editorial_technical_team'),
        people: [
          {
            firstName: 'Serge',
            lastName: 'Lemouton',
          },
        ],
      },
    ],
  },
]
onBeforeMount(() => {
  const anchor = ref(null)
  const route = useRoute()
  if (route.hash === '#media_library' && anchor.value) {
    anchor.value.scrollIntoView({ behavior: 'smooth' })
  }
})

const latestPublicationsGroups = [
  {
    title: 'Biographies',
    titleEn: 'Biographies',
    filterFn: ({ type }) => type === 'biography',
  },
  {
    title: 'Parcours et Analyses',
    titleEn: 'Surveys of works and Analyses',
    filterFn: ({ type }) => type === 'analysis' || type === 'workcourse',
  },
  {
    title: 'Concerts',
    titleEn: 'Concerts',
    filterFn: ({ subType }) =>
      subType === 'Archives concerts' || subType === 'Concert',
  },
  {
    title: 'Conférences',
    titleEn: 'Conferences',
    filterFn: ({ subType }) =>
      subType === 'Conférences' || subType === 'Séminaire / Conférence',
  },
  {
    title: 'Autres',
    titleEn: 'Others',
    filterFn: ({ type, subType }) =>
      type !== 'analysis' &&
      type !== 'workcourse' &&
      type !== 'biography' &&
      subType !== 'Archives concerts' &&
      subType !== 'Conférences' &&
      subType !== 'Séminaire / Conférence' &&
      subType !== 'Concert',
  },
]
</script>

<template>
  <div>
    <div>
      <PatternHero size="medium" color-class="text-primary-400">
        <div class="flex h-full flex-col justify-center p-6 lg:p-10">
          <h1
            class="text-mobile-3xl font-semibold capitalize md:text-3xl"
            v-text="$t('labels.resource_center')"
          ></h1>
          <p
            class="mt-2 text-mobile-lg md:text-lg"
            v-text="$t('descriptions.resource_center')"
          ></p>
          <SearchView :inHome="true" class="mt-6" />
        </div>
      </PatternHero>
    </div>

    <div class="mx-auto max-w-7xl py-10 px-6 md:py-12 md:px-4">
      <section>
        <h2 class="text-mobile-2xl font-semibold md:text-2xl">
          {{ $t('index.focus_title') }}
        </h2>
        <span
          v-if="locale === 'fr'"
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="focusText"
        />
        <span
          v-else
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="focusTextEn"
        />

        <div>
          <carousel
            :items-to-show="2"
            :itemsToScroll="1"
            :breakpoints="breakpoints"
          >
            <slide v-for="card in focuses" :key="card.id">
              <CardResource :key="card.id" :resource="card" />
            </slide>

            <template #addons>
              <navigation />
              <!-- <pagination /> -->
            </template>
          </carousel>
        </div>
      </section>
      <section class="mt-10 md:mt-20" v-if="exhibition.length > 0">
        <div class="flex items-center justify-between">
          <h2 class="text-mobile-2xl font-semibold md:text-2xl">
            {{ $t('index.exhibition_title') }}
          </h2>
        </div>
        <span
          v-if="locale === 'fr'"
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="exhibitionText"
        />
        <span
          v-else
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="exhibitionTextEn"
        />
        <div>
          <carousel
            :items-to-show="2"
            :itemsToScroll="1"
            :breakpoints="breakpoints"
          >
            <slide v-for="card in exhibition" :key="card.id">
              <CardResource :key="card.id" :resource="card" />
            </slide>

            <template #addons>
              <navigation />
              <!-- <pagination /> -->
            </template>
          </carousel>
        </div>
      </section>
      <section class="mt-10 md:mt-20">
        <div class="flex items-center justify-between">
          <h2 class="text-mobile-2xl font-semibold md:text-2xl">
            {{ $t('index.latest_publications_title') }}
          </h2>
        </div>
        <div id="latest_publications" class="-mt-24 mb-36" />
        <div v-for="publicationGroup in latestPublicationsGroups">
          <div
            v-if="
              latestPublications.filter(publicationGroup.filterFn).length > 0
            "
          >
            <h2
              class="text-mobile-lg font-semibold md:text-lg"
              v-if="locale === 'fr'"
            >
              {{ publicationGroup.title }}
            </h2>
            <h2 class="text-mobile-lg font-semibold md:text-lg" v-else>
              {{ publicationGroup.titleEn }}
            </h2>
            <carousel
              :items-to-show="2"
              :itemsToScroll="1"
              :breakpoints="breakpoints"
              v-if="
                latestPublications.filter(publicationGroup.filterFn).length > 0
              "
            >
              <slide
                v-for="card in latestPublications.filter(
                  publicationGroup.filterFn
                )"
                :key="card.id"
              >
                <CardResource :key="card.id" :resource="card" />
              </slide>

              <template #addons>
                <navigation />
                <!-- <pagination /> -->
              </template>
            </carousel>
          </div>
        </div>
        <!-- <div> -->
        <!--   <carousel -->
        <!--     :items-to-show="2" -->
        <!--     :itemsToScroll="1" -->
        <!--     :breakpoints="breakpoints" -->
        <!--   > -->
        <!--     <slide v-for="card in latestPublications" :key="card.id"> -->
        <!--       <CardResource :key="card.id" :resource="card" /> -->
        <!--     </slide> -->
        <!---->
        <!--     <template #addons> -->
        <!--       <navigation /> -->
        <!--     </template> -->
        <!--   </carousel> -->
        <!-- </div> -->
      </section>
      <section class="mt-10 md:mt-20">
        <div class="flex items-center justify-between">
          <h2 class="text-mobile-2xl font-semibold md:text-2xl">
            {{ $t('index.flora_title') }}
          </h2>
        </div>
        <span
          v-if="locale === 'fr'"
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="floraText"
        />
        <span
          v-else
          class="mt-2 text-mobile-lg md:mt-4 md:text-lg markdown-links"
          v-html="floraTextEn"
        />
        <div>
          <carousel
            :items-to-show="2"
            :itemsToScroll="1"
            :breakpoints="breakpoints"
          >
            <slide v-for="card in floras" :key="card.id">
              <CardResource :key="card.id" :resource="card" />
            </slide>

            <template #addons>
              <navigation />
              <!-- <pagination /> -->
            </template>
          </carousel>
        </div>
      </section>
    </div>
    <div class="relative">
      <!-- container for the absolute sidebar in desktop -->
      <div class="pointer-events-none absolute hidden h-full w-full md:block">
        <div
          class="mx-auto flex h-full max-w-7xl items-start gap-16 px-4 pt-20"
        >
          <div class="h-full flex-1 basis-2/3"></div>
          <HomeNavAbout
            class="pointer-events-auto sticky top-44 z-10 hidden max-w-sm flex-1 basis-1/3 md:block"
          />
        </div>
      </div>
      <div class="bg-gradient-to-b from-primary-300 to-primary-600">
        <section class="mx-auto max-w-7xl p-6 text-shade-50 md:py-20 md:px-4">
          <div class="flex items-center gap-16">
            <div class="flex-1 basis-2/3">
              <h2 class="text-mobile-2xl font-semibold md:text-2xl">
                {{ $t('index.about_title') }}
              </h2>
              <p class="mt-4 text-mobile-lg md:text-lg">
                {{ $t('index.about_description') }}
              </p>
            </div>
            <!-- Reserved space for the "absolute sidebar" -->
            <div
              class="hidden h-[500px] max-w-sm flex-1 basis-1/3 opacity-0 md:block"
            ></div>
          </div>
        </section>
        <PatternHero
          :custom-pattern-height="3"
          color-class="text-shade-50"
          class="mt-6"
        />
      </div>
      <div class="mx-auto max-w-7xl px-6 pt-6 md:px-4 md:pt-20">
        <div class="block md:flex md:items-start md:gap-8 lg:gap-16">
          <HomeNavAbout
            class="md:order-last md:hidden md:flex-1 md:basis-1/3"
          />
          <!-- Reserved space for the "absolute sidebar" -->
          <div
            class="hidden md:order-last md:block md:flex-1 md:basis-1/3"
          ></div>
          <div class="flex-1 basis-2/3">
            <div id="resource_type" class="-mt-24 mb-36" />
            <DataReadMore
              title="index.resources_types.title"
              class="mt-6 md:mt-0"
              :hide-show-more="true"
            >
              <template #excerpt>
                <div>
                  {{ $t('index.resources_types.description') }}
                </div>
                <ul>
                  <li>
                    {{ $t('index.resources_types.point_1') }}
                  </li>
                  <li>{{ $t('index.resources_types.point_2') }}</li>
                </ul>
              </template>
            </DataReadMore>
            <div id="media_library" class="-mt-24 mb-36" />
            <DataReadMore
              ref="anchor"
              title="index.collections.title"
              class="mt-6 md:mt-8 lg:mt-16"
            >
              <template #excerpt>
                <p class="font-semibold">
                  {{ $t('index.collections.terms') }}
                </p>
              </template>
              <h3 class="mt-2">{{ $t('index.collections.subtitle') }}</h3>
              <p>
                {{ $t('index.collections.sentence') }}
              </p>
              <a
                href="https://www.ircam.fr/media/uploads/transmission/Ressources/mediatheque_nouveautes_2023.pdf"
              >
                {{ $t('index.collections.link') }}
              </a>
              <p>
                {{ $t('index.collections.description') }}
              </p>
              <p>
                {{ $t('index.collections.description_2') }}
              </p>
              <p>
                {{ $t('index.collections.description_3') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.collections.list') }}
                </li>
              </ul>
              <p>
                {{ $t('index.collections.description_4') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.collections.list_2') }}
                </li>
                <li>
                  {{ $t('index.collections.list_3') }}
                </li>
                <li>
                  {{ $t('index.collections.list_4') }}
                </li>
                <li>
                  {{ $t('index.collections.list_5') }}
                </li>
              </ul>

              <h3 class="mt-2">{{ $t('index.collections.subtitle_2') }}</h3>
              <p>
                {{ $t('index.collections.description_5') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.collections.list_6') }}
                </li>
                <li>
                  {{ $t('index.collections.list_7') }}
                </li>
              </ul>
              <h3 class="mt-2">{{ $t('index.collections.subtitle_3') }}</h3>
              <p>
                {{ $t('index.collections.description_6') }}
              </p>
              <p>
                {{ $t('index.collections.description_7') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.collections.list_8') }}
                </li>
                <li>
                  {{ $t('index.collections.list_9') }}
                </li>
                <li>
                  {{ $t('index.collections.list_11')
                  }}<span
                    ><a
                      href="https://www.ircam.fr/media/uploads/transmission/Ressources/formulaire_adhesion_mediatheque.pdf"
                    >
                      {{ $t('index.collections.link_2') }}
                    </a></span
                  >
                </li>
              </ul>
              <p>
                {{ $t('index.collections.description_8') }}
              </p>
              <h3 class="mt-2">{{ $t('index.collections.subtitle_4') }}</h3>
              <ul>
                <li>
                  {{ $t('index.collections.list_12') }}
                </li>
                <li>
                  {{ $t('index.collections.list_14') }}
                </li>
              </ul>
              <h3 class="mt-2">{{ $t('index.collections.subtitle_5') }}</h3>
              <p>
                {{ $t('index.collections.description_9') }}
              </p>
              <p>
                {{ $t('index.collections.description_10') }}
              </p>
              <div v-for="(base, i) in library" :key="i">
                <div v-if="i === 0">
                  <h3 class="first-letter:uppercase">
                    {{ base.title }}
                  </h3>
                  <div v-for="(group, j) in base.groups" :key="j">
                    <div v-if="j === 0 || j === 1">
                      <h4 class="first-letter:uppercase">
                        {{ group.title }}
                      </h4>
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-for="(person, k) in group.people"
                          :key="k"
                          class="flex-1 basis-full md:flex-initial md:basis-auto"
                        >
                          <CardPerson
                            v-if="k === 0 || k === 1"
                            :person="person"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DataReadMore>
            <div id="historical_structure" class="-mt-24 mb-36" />
            <DataReadMore
              title="index.historical_structure.title"
              class="mt-6 md:mt-8 lg:mt-16"
            >
              <template #excerpt>
                {{ $t('index.historical_structure.description') }}
                <ul>
                  <li>
                    {{ $t('index.historical_structure.point_1') }}
                    <b>{{
                      $t('index.historical_structure.brahms_name_bold')
                    }}</b>
                    {{ $t('index.historical_structure.brahms_name') }}
                  </li>
                  <li>
                    <b> {{ $t('index.historical_structure.base') }}</b>
                    <b>{{ $t('index.historical_structure.analyses_name') }}</b>
                    {{ $t('index.historical_structure.point_2') }}
                  </li>
                  <li>
                    <b> {{ $t('index.historical_structure.base') }}</b>
                    <b>{{ $t('index.historical_structure.medias_name') }}</b>
                    {{ $t('index.historical_structure.point_3') }}
                  </li>
                  <li>
                    <b> {{ $t('index.historical_structure.base') }}</b>
                    <b>{{ $t('index.historical_structure.sidney_name') }}</b>
                    {{ $t('index.historical_structure.point_4') }}
                  </li>
                </ul>
              </template>
              <h3 class="mt-2">
                {{ $t('index.historical_structure.subtitle') }}
              </h3>
              <b>
                {{ $t('index.historical_structure.subsubtitle_1') }}
              </b>
              <p>
                {{ $t('index.historical_structure.text_1') }}
              </p>
              <b>
                {{ $t('index.historical_structure.subsubtitle_2') }}
              </b>
              <p>
                {{ $t('index.historical_structure.text_2') }}
              </p>
              <b>
                {{ $t('index.historical_structure.subsubtitle_3') }}
              </b>
              <p>
                {{ $t('index.historical_structure.text_3') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.historical_structure.point_5') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_6') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_7') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_8') }}
                </li>
              </ul>
              <p>
                {{ $t('index.historical_structure.description_3') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.historical_structure.point_9') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_10') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_11') }}
                </li>
              </ul>
              <b>
                {{ $t('index.historical_structure.subsubtitle_4') }}
              </b>
              <p>
                {{ $t('index.historical_structure.description_4') }}
              </p>
              <h3 class="mt-2">
                {{ $t('index.historical_structure.subtitle_2') }}
              </h3>
              <p>
                {{ $t('index.historical_structure.description_5') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.historical_structure.point_12') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_13') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_14') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_15') }}
                </li>
              </ul>
              <h3 class="mt-2">
                {{ $t('index.historical_structure.subtitle_3') }}
              </h3>
              <p>
                {{ $t('index.historical_structure.description_6') }}
              </p>
              <p>
                {{ $t('index.historical_structure.description_8') }}
                <a href="mailto:sidney@ircam.fr">
                  {{ $t('index.historical_structure.link_1') }}
                </a>
              </p>
              <h3 class="mt-2">
                {{ $t('index.historical_structure.subtitle_4') }}
              </h3>
              <p>
                {{ $t('index.historical_structure.description_7') }}
              </p>
              <ul>
                <li>
                  {{ $t('index.historical_structure.point_16') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_17') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_18') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_19') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_20') }}
                </li>
                <li>
                  {{ $t('index.historical_structure.point_21') }}
                </li>
              </ul>
            </DataReadMore>
            <div id="credits" class="-mt-24 mb-36" />
            <DataReadMore
              title="index.credits.title"
              class="mt-6 md:mt-8 lg:mt-16"
            >
              <template #excerpt>
                {{ $t('index.credits.description') }}<br /><br />
                {{ $t('index.credits.description_1') }}<br /><br />
                {{ $t('index.credits.description_3') }}
                <a href="#historical_structure">{{
                  $t('index.credits.link')
                }}</a
                >.
                <div v-for="(base, i) in credits" :key="i">
                  <div v-if="i === 0">
                    <h3 class="first-letter:uppercase">
                      {{ base.title }}
                    </h3>
                    <p>{{ base.subtext }}</p>
                    <div v-for="(group, j) in base.groups" :key="j">
                      <div v-if="j === 0 || j === 1">
                        <h4 class="first-letter:uppercase">
                          {{ group.title }}
                        </h4>
                        <div class="flex flex-wrap gap-2">
                          <div
                            v-for="(person, k) in group.people"
                            :key="k"
                            class="flex-1 basis-full md:flex-initial md:basis-auto"
                          >
                            <CardPerson
                              v-if="k === 0 || k === 1"
                              :person="person"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <div v-for="(base, i) in credits" :key="i">
                <h3 v-if="i !== 0" class="first-letter:uppercase">
                  {{ base.title }}
                </h3>
                <div v-for="(group, j) in base.groups" :key="j">
                  <h4
                    v-if="!(i === 0 && j === 0) && !(i === 0 && j === 1)"
                    class="first-letter:uppercase"
                  >
                    {{ group.title }}
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="(person, k) in group.people"
                      :key="k"
                      class="flex-1 basis-full md:flex-initial md:basis-auto"
                    >
                      <CardPerson
                        v-if="
                          !(i === 0 && j === 0 && k === 0) &&
                          !(i === 0 && j === 1 && k === 0) &&
                          !(i === 0 && j === 1 && k === 1)
                        "
                        :person="person"
                      />
                    </div>
                    <div v-if="group.title === t('index.credits.translators')">
                      <p>
                        {{ $t('index.credits.sentence_4') }}
                      </p>
                      <p>
                        {{ $t('index.credits.sentence_5') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <h3>{{ $t('index.credits.copyrights') }}</h3>
              <p>
                {{ $t('index.credits.sentence') }}
                <a href="#">{{ $t('index.credits.link_2') }}</a>
                {{ $t('index.credits.sentence_2') }}
              </p>
              <p>
                {{ $t('index.credits.sentence_3') }}
                <a href="mailto:ressources-contenus@ircam.fr">{{
                  $t('index.credits.link_3')
                }}</a>
              </p>
            </DataReadMore>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="showPopup"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="closePopup"
  >
    <div
      class="bg-white p-6 max-w-3xl shadow-md shadow-primary-400 relative overflow-y-scroll"
    >
      <button
        @click="closePopup"
        class="absolute top-32 right-2 hover:text-gray-700 text-xl"
      >
        ✕
      </button>
      <h2 class="text-xl font-bold mb-4">{{ isFrench ? frTitle : enTitle }}</h2>
      <div v-if="isFrench" class="whitespace-pre-line mb-6">
        Toutes les bases de données documentaires de l’Ircam ont été fusionnées
        en une seule interface.
        <br /><br />
        Vous y trouverez, accessibles par une même barre de recherche :<br />
        <br />
        <ul class="list-disc md:ml-6">
          <li>
            le catalogue des documents présents à la médiathèque de l’Ircam
          </li>
          <li>
            les entrées de l'encyclopédie sur les compositeurs et compositrices
            de musique contemporaine
          </li>
          <li>les archives audiovisuelles de l’Institut</li>
          <li>les analyses d’œuvres Ircam</li>
          <li>les articles scientifiques issus du laboratoire depuis 1977</li>
        </ul>
        <br /><br />
        Si vous rencontrez des difficultés d’utilisation, vous pouvez contacter
        l’équipe du Pôle documentaire à<br />
        <a
          class="text-primary-400 underline"
          href="mailto:info-mediatheque@ircam.fr"
          >info-mediatheque@ircam.fr</a
        >
      </div>
      <div v-else class="text-gray-700 whitespace-pre-line mb-6">
        All Ircam's documentary databases have been merged into a single
        interface.<br /><br />
        You will find, accessible via the same search bar:<br /><br />
        <ul class="list-disc md:ml-6">
          <li>the media library’s catalogue</li>
          <li>
            entries from the encyclopaedia of contemporary music composers
          </li>
          <li>the Institute's audiovisual archive</li>
          <li>analyses of Ircam works</li>
          <li>scientific articles produced by the research teams since 1977</li>
        </ul>
        <br /><br />
        If you encounter any problems using the database, please contact the
        Documentation Center team at<br />
        <a
          class="text-primary-400 underline"
          href="mailto:info-mediatheque@ircam.fr"
          >info-mediatheque@ircam.fr</a
        >
      </div>
      <div class="flex justify-center space-x-4 text-gray-500">
        <button
          @click="toggleLanguage"
          class="focus:outline-none"
          :class="{ underline: isFrench }"
        >
          EN
        </button>
        <span>|</span>
        <button
          @click="toggleLanguage"
          class="focus:outline-none"
          :class="{ underline: !isFrench }"
        >
          FR
        </button>
      </div>
    </div>
  </div>
</template>
<style>
.carousel__slide {
  display: block;
  padding: 10px;
}

.carousel__icon {
  background-color: white;
  fill: orangered;
}

.markdown-links a {
  color: rgba(242, 98, 36, 1);
}

.markdown-links a:hover {
  text-decoration: underline;
}
</style>
